/**
 * SPR-10015: the date of a support request's supporting document, and whether it is still
 * valid for the request.
 *
 * A document is valid when its date is within three months either side of the submission
 * date. Which date that is depends on the goal:
 *  - goals that require a document (medical, dental, unusual expenses...): the date the
 *    coordinator typed for the document, stored as `additionalDetails.documentDate`;
 *  - Wedding: the wedding date (`additionalDetails.weddingDate`; older requests only carry
 *    it as a «תאריך חתונה: DD/MM/YYYY» user comment);
 *  - BarMitzvah: nothing is typed - the date is the child's 13th Hebrew birthday.
 *
 * All dates are civil `YYYY-MM-DD` strings.
 */
import { HDate } from '@hebcal/hdate';
import AllowedFor from '@/models/common/AllowedFor';
import { civilIsoFromLocalDate, civilIsoFromParts, toJerusalemCivilIso } from '@/helpers/hebrewDateHelper';

export const DOCUMENT_VALIDITY_MONTHS = 3;

const WEDDING_GOAL = 'Wedding';
const BAR_MITZVAH_GOAL = 'BarMitzvah';
const BAR_MITZVAH_AGE = 13;
const ADAR = 12;
const ADAR_II = 13;

export type DocumentDateKind = 'document' | 'wedding' | 'barMitzvah';

interface GoalLike {
  name?: string;
  allowedFor?: string;
  requiredDocuments?: string[];
}

/** The goals whose form asks for a separate document date. Wedding has its own wedding date. */
export const requiresDocumentDate = (goal: GoalLike | null | undefined): boolean =>
  !!goal &&
  goal.allowedFor !== AllowedFor.Fund &&
  goal.name !== WEDDING_GOAL &&
  (goal.requiredDocuments?.length ?? 0) > 0;

const localDateFromIso = (iso: string): Date => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Shift a civil date by whole months; the 31st lands on the last day of a shorter month. */
const shiftMonths = (iso: string, months: number): string => {
  const [year, month, day] = iso.split('-').map(Number);
  const lastDay = new Date(year, month - 1 + months + 1, 0).getDate();
  return civilIsoFromLocalDate(new Date(year, month - 1 + months, Math.min(day, lastDay)));
};

/** True when `documentIso` is within three months before or after `submissionIso`, both inclusive. */
export const isDocumentDateValid = (documentIso: string, submissionIso: string): boolean =>
  documentIso >= shiftMonths(submissionIso, -DOCUMENT_VALIDITY_MONTHS) &&
  documentIso <= shiftMonths(submissionIso, DOCUMENT_VALIDITY_MONTHS);

const hebrewMonthOfBarMitzvah = (birthMonth: number, birthYear: number, barMitzvahYear: number): number => {
  const bornInAdar = birthMonth === ADAR_II || (birthMonth === ADAR && !HDate.isLeapYear(birthYear));
  if (bornInAdar) {
    // Plain Adar and Adar II are celebrated in Adar II of a leap year; Adar I stays Adar I.
    return HDate.isLeapYear(barMitzvahYear) ? ADAR_II : ADAR;
  }
  return birthMonth;
};

/**
 * The child's 13th birthday on the Hebrew calendar, as a civil date. A birthday on the 30th of
 * a month that has only 29 days that year falls on the 1st of the next month.
 */
export const barMitzvahDate = (birthDate: unknown): string | null => {
  const birthIso = toJerusalemCivilIso(birthDate);
  if (!birthIso) return null;
  try {
    const birth = new HDate(localDateFromIso(birthIso));
    const year = birth.getFullYear() + BAR_MITZVAH_AGE;
    const month = hebrewMonthOfBarMitzvah(birth.getMonth(), birth.getFullYear(), year);
    const firstOfMonth = new HDate(1, month, year);
    const greg = new HDate(firstOfMonth.abs() + birth.getDate() - 1).greg();
    return civilIsoFromParts(greg.getFullYear(), greg.getMonth() + 1, greg.getDate());
  } catch {
    return null;
  }
};

const WEDDING_COMMENT = /תאריך חתונה:\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/;

const weddingDateFromComments = (comments: string[] | undefined): string | null => {
  for (const comment of comments ?? []) {
    const match = WEDDING_COMMENT.exec(comment);
    if (match) return civilIsoFromParts(Number(match[3]), Number(match[2]), Number(match[1]));
  }
  return null;
};

const stringField = (details: object | undefined, key: string): string | null => {
  const value = (details as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
};

interface RequestLike {
  goal?: GoalLike | null;
  additionalDetails?: object;
  userComments?: string[];
  requestedAt?: string;
  requestedBecauseOfContact?: { birthDate?: unknown } | null;
}

export interface RequestDocumentDate {
  kind: DocumentDateKind;
  date: string;
  /** Null when the submission date is unknown. */
  isValid: boolean | null;
}

/** The request's document date and its validity, or null when the goal has none or it is missing. */
export const resolveRequestDocumentDate = (request: RequestLike): RequestDocumentDate | null => {
  const goalName = request.goal?.name;
  let kind: DocumentDateKind;
  let date: string | null;

  if (goalName === BAR_MITZVAH_GOAL) {
    kind = 'barMitzvah';
    date = barMitzvahDate(request.requestedBecauseOfContact?.birthDate);
  } else if (goalName === WEDDING_GOAL) {
    kind = 'wedding';
    date = stringField(request.additionalDetails, 'weddingDate') ?? weddingDateFromComments(request.userComments);
  } else if (requiresDocumentDate(request.goal)) {
    kind = 'document';
    date = stringField(request.additionalDetails, 'documentDate');
  } else {
    return null;
  }

  if (!date) return null;
  const submissionIso = toJerusalemCivilIso(request.requestedAt);
  return { kind, date, isValid: submissionIso ? isDocumentDateValid(date, submissionIso) : null };
};
